import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { DECISION_TYPE, ISSUE_TYPE, OPPORTUNITY_TYPE, RISK_TYPE, SOURCE_TYPE_DEPARTMENT, SOURCE_TYPE_MASTER, SOURCE_TYPE_VENUE } from '../constants';
import { UPSERT_DECISION_MODAL_ID } from '../register/general_decision/GeneralDecisionsIndex';
import { UPSERT_ISSUE_MODAL_ID } from '../register/general_issue/GeneralIssuesIndex';
import { UPSERT_OPPORTUNITY_MODAL_ID } from '../register/general_opportunities/GeneralOpportunitiesIndex';
import { UPSERT_RISK_MODAL_ID } from '../register/general_risk/GeneralRisksIndex';

import useQuery from 'Hooks/useQuery';

const RaidContext = createContext();

export const useRaidContext = () => {
    const context = useContext(RaidContext);

    if (!context) {
        throw new Error('useRaidContext must be used within a RaidContext');
    }

    return context;
};

const getQuery = () => {
    const queryParams = new URLSearchParams(window.location.search);
    return {
        type: queryParams.get('type'),
        id: queryParams.get('id'),
        budget_department_type: queryParams.get('budget_department_type'),
        budget_department_id: queryParams.get('budget_department_id'),
    };
};

export const RaidProvider = ({
    children,
    source = SOURCE_TYPE_MASTER,
    venue,
    // Department source only: `{ id, name, type }`, where `type` is the model
    // class the budget-department endpoints key on. Optional, so the central and
    // venue mounts are unaffected.
    budgetDepartment,
    canManageRisks,
    canManageIssues,
    canManageDecisions,
    canCreateOpportunities,
    canEditOpportunities,
    canDeleteOpportunities,
    routes,
}) => {
    const [risks, setRisks] = useState([]);
    const [trashedRisks, setTrashedRisks] = useState([]);
    const [issues, setIssues] = useState([]);
    const [decisions, setDecisions] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [trashedOpportunities, setTrashedOpportunities] = useState([]);
    const [types, setTypes] = useState([]);
    const [impacts, setImpacts] = useState([]);
    const [probabilities, setProbabilities] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [impactCategories, setImpactCategories] = useState([]);
    const [departmentsOptions, setDepartmentsOptions] = useState([]);
    const [loadingDatasets, setLoadingDatasets] = useState(false);
    const [modalsVisibility, setVisibleModals] = useState({});
    const [selectedRisk, setSelectedRisk] = useState(null);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [selectedDecision, setSelectedDecision] = useState(null);
    const [selectedOpportunity, setSelectedOpportunity] = useState(null);

    const isMasterSource = useMemo(() => source === SOURCE_TYPE_MASTER, [source]);
    const isVenueSource = useMemo(() => source === SOURCE_TYPE_VENUE, [source]);
    const isDepartmentSource = useMemo(() => source === SOURCE_TYPE_DEPARTMENT, [source]);

    // The "Type::ID" composite the general-risk endpoints filter on.
    const budgetDepartmentKey = useMemo(() => (budgetDepartment ? `${budgetDepartment.type}::${budgetDepartment.id}` : null), [budgetDepartment]);

    // Sent on every register read so a department source only ever receives its
    // own department's records, never the whole event's.
    const sourceParams = useMemo(
        () => (isDepartmentSource ? { budget_department_key: budgetDepartmentKey } : { venue_id: venue?.id }),
        [isDepartmentSource, budgetDepartmentKey, venue?.id],
    );

    const venuesOptionsRequest = useQuery({
        enabled: isMasterSource, // Only fetch venues options if the source is master, as they are not needed for venue source
        queryFn: () => axios.get(routes.fetchVenuesOptionsUrl, { params: { grouped: true, use_id_as_value: true } }),
    });

    useEffect(() => {
        fetchDatasets();
    }, []);

    useEffect(() => {
        if (selectedIssue || selectedDecision) {
            pushUrlToOriginal();
        }
    }, [selectedIssue, selectedDecision]);

    const fetchGeneralRisks = (filters) => {
        return axios.get(routes.fetchGeneralRisksUrl, { params: { ...filters, ...sourceParams } }).then(({ data }) => {
            setRisks([...data]);
        });
    };

    const fetchTrashedGeneralRisks = () => {
        if (!routes.fetchTrashedGeneralRisksUrl) return Promise.resolve();
        return axios.get(routes.fetchTrashedGeneralRisksUrl, { params: { venue_id: venue?.id } }).then(({ data }) => {
            setTrashedRisks([...data]);
        });
    };

    const fetchGeneralIssues = (filters) => {
        return axios.get(routes.fetchGeneralIssuesUrl, { params: { ...filters, venue_id: venue?.id } }).then(({ data }) => {
            setIssues([...data]);
        });
    };

    const fetchGeneralDecisions = (filters) => {
        return axios.get(routes.fetchGeneralDecisionsUrl, { params: { ...filters, venue_id: venue?.id } }).then(({ data }) => {
            setDecisions([...data]);
        });
    };

    const fetchGeneralOpportunities = () => {
        return axios.get(routes.fetchGeneralOpportunitiesUrl, { params: { venue_id: venue?.id } }).then(({ data }) => {
            setOpportunities([...data.data]);
        });
    };

    const fetchTrashedGeneralOpportunities = () => {
        if (!routes.fetchTrashedGeneralOpportunitiesUrl) return Promise.resolve();
        return axios.get(routes.fetchTrashedGeneralOpportunitiesUrl).then(({ data }) => {
            setTrashedOpportunities([...data]);
        });
    };

    const fetchDatasets = () => {
        setLoadingDatasets(true);
        const query = getQuery();

        const urls = [
            routes.fetchGeneralRiskTypesUrl,
            routes.fetchGeneralRiskImpactsUrl,
            routes.fetchGeneralImpactCategoriesUrl,
            routes.fetchGeneralRiskProbabilitiesUrl,
            routes.fetchGeneralRiskStatusesUrl,
            routes.fetchEventDepartmentsOptionsUrl,
        ];
        // A department source carries its budget department on the mount rather
        // than in the URL, since nothing navigated here to put it there.
        const params = {
            [routes.fetchEventDepartmentsOptionsUrl]: isDepartmentSource
                ? {
                      budget_department_type: budgetDepartment?.type,
                      budget_department_id: budgetDepartment?.id,
                  }
                : {
                      budget_department_type: query.budget_department_type,
                      budget_department_id: query.budget_department_id,
                  },
        };
        const requests = urls.map((url) => axios.get(url, params[url] ? { params: params[url] } : undefined));
        Promise.all(requests)
            .then(([types, impacts, impactCategories, probabilities, statuses, departmentsOptions]) => {
                setTypes(types.data);
                setImpacts(impacts.data);
                setProbabilities(probabilities.data);
                setStatuses(statuses.data);
                setImpactCategories(impactCategories.data);
                setDepartmentsOptions(departmentsOptions.data);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => setLoadingDatasets(false));
    };

    const pushUrlToOriginal = () => {
        const query = getQuery();
        if (query.type && history.pushState) {
            const newurl = window.location.href.split('?')[0];
            window.history.pushState({ path: newurl }, '', newurl);
        }
    };

    const toggleModalVisibility = useCallback((modalName, visible) => {
        setVisibleModals((prev) => ({
            ...prev,
            [modalName]: visible !== undefined ? visible : !prev[modalName],
        }));
    }, []);

    const showModalByRef = useCallback(
        (type) => {
            switch (type) {
                case RISK_TYPE:
                    toggleModalVisibility(UPSERT_RISK_MODAL_ID, true);
                    break;
                case ISSUE_TYPE:
                    toggleModalVisibility(UPSERT_ISSUE_MODAL_ID, true);
                    break;
                case DECISION_TYPE:
                    toggleModalVisibility(UPSERT_DECISION_MODAL_ID, true);
                    break;
                case OPPORTUNITY_TYPE:
                    toggleModalVisibility(UPSERT_OPPORTUNITY_MODAL_ID, true);
                    break;
            }
        },
        [toggleModalVisibility],
    );

    const createByType = useCallback(
        (type) => {
            switch (type) {
                case RISK_TYPE:
                    if (!canManageRisks) {
                        return;
                    }

                    setSelectedRisk(null);
                    showModalByRef(type);
                    break;
                case ISSUE_TYPE:
                    if (!canManageIssues) {
                        return;
                    }

                    setSelectedIssue(null);
                    showModalByRef(type);
                    break;
                case DECISION_TYPE:
                    if (!canManageDecisions) {
                        return;
                    }

                    setSelectedDecision(null);
                    showModalByRef(type);
                    break;
                case OPPORTUNITY_TYPE:
                    if (!canCreateOpportunities) {
                        return;
                    }

                    setSelectedOpportunity(null);
                    showModalByRef(type);
                    break;
            }
        },
        [canManageIssues, canManageDecisions, canManageRisks, canCreateOpportunities, showModalByRef],
    );

    const importByType = useCallback(
        (type) => {
            const urlsMap = {
                [RISK_TYPE]: routes.importGeneralRisksUrl,
                [ISSUE_TYPE]: routes.importGeneralIssuesUrl,
                [DECISION_TYPE]: routes.importGeneralDecisionsUrl,
                [OPPORTUNITY_TYPE]: routes.importGeneralOpportunitiesUrl,
            };

            const importUrl = urlsMap[type];

            if (importUrl) {
                window.location.href = importUrl;
            }
        },
        [routes],
    );

    const value = {
        isMasterSource,
        isVenueSource,
        risks,
        trashedRisks,
        issues,
        decisions,
        opportunities,
        trashedOpportunities,
        types,
        impacts,
        probabilities,
        statuses,
        impactCategories,
        departmentsOptions,
        venuesOptions: venuesOptionsRequest.data || [],
        loadingDatasets,
        canManageRisks,
        canManageIssues,
        canManageDecisions,
        canEditOpportunities,
        canDeleteOpportunities,
        currentVenue: venue,
        currentBudgetDepartment: budgetDepartment,
        budgetDepartmentKey,
        isDepartmentSource,
        routes,
        fetchGeneralRisks,
        fetchTrashedGeneralRisks,
        fetchGeneralIssues,
        fetchGeneralDecisions,
        fetchGeneralOpportunities,
        fetchTrashedGeneralOpportunities,
        pushUrlToOriginal,
        getQuery,
        fetchDatasets,
        modalsVisibility,
        toggleModalVisibility,
        showModalByRef,
        selectedRisk,
        setSelectedRisk,
        selectedIssue,
        setSelectedIssue,
        selectedDecision,
        setSelectedDecision,
        selectedOpportunity,
        setSelectedOpportunity,
        createByType,
        importByType,
    };

    return <RaidContext.Provider value={value}>{children}</RaidContext.Provider>;
};

export default RaidContext;
